import React, { ComponentProps, useCallback, useEffect, useRef, useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import AutorenewIcon from '@mui/icons-material/Autorenew';
import BugReportIcon from '@mui/icons-material/BugReport';
import { Box, Button, Chip, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import { HERO_PLANNER_DEBUG_PATH, HERO_PLANNER_DEBUG_SEARCH_PARAMS } from '~/consts/customerCommunication/heroPlanner';
import { ROLE_HERO_PLANNER_ADMIN } from '~/consts/roles';

import { useAppSelector } from '~/hooks/store';
import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';
import useToggleState from '~/hooks/useToggleState';

import { Dayjs, getDayJs } from '~/services/TimeService';

import { canRegenerateHeroPlannerTailDeals } from '~/utils/canRegenerateHeroPlannerTailDeals';

import RegenerateTailDealsDialog from './RegenerateTailDealsDialog';
import SchedulesCountryGroupsTabs from './ScheduleFilters/SchedulesCountryGroupsTabs';
import SchedulesCountryStateTabs from './ScheduleFilters/SchedulesCountryStateTabs';

export enum SCHEDULE_SEARCH_PARAMS {
  FROM_DATE = 'from-date',
  TO_DATE = 'to-date',
  VIEW = 'view',
  COUNTRY_GROUP = 'country-group',
  COUNTRY = 'country',
  COUNTRY_STATE = 'country-state',
}

export const SCHEDULE_SEARCH_PARAMS_STORAGE_KEY = 'customer-communications-schedules-filters';

function isCountryGroupsFilterSupportedForBrand(brand: string) {
  return brand === 'luxuryescapes' || brand === 'treatmetravel' || brand === 'leagenthub';
}

export function isCountryStateFilterSupportedForBrand(brand: string) {
  return brand !== 'luxuryescapes';
}

const VIEWS = [
  { id: 'email', name: 'Email' },
  { id: 'push', name: 'Push' },
  { id: 'deals', name: 'Deals' },
];

const DATE_PRESETS = [
  {
    label: 'Today',
    getRange: (): DateRange<Dayjs> => {
      const today = getDayJs().tz(window.configs.DEFAULT_TIMEZONE);
      return [today, today];
    },
  },
  {
    label: 'Tomorrow',
    getRange: (): DateRange<Dayjs> => {
      const tomorrow = getDayJs().tz(window.configs.DEFAULT_TIMEZONE).add(1, 'day');
      return [tomorrow, tomorrow];
    },
  },
  {
    label: 'This Week',
    getRange: (): DateRange<Dayjs> => {
      const today = getDayJs().tz(window.configs.DEFAULT_TIMEZONE);
      return [today, today.endOf('week')];
    },
  },
  {
    label: 'Next 7 Days',
    getRange: (): DateRange<Dayjs> => {
      const today = getDayJs().tz(window.configs.DEFAULT_TIMEZONE);
      return [today, today.add(7, 'day')];
    },
  },
];

function SchedulesFilters() {
  const brand = useAppSelector((state) => state.tenant.brand);
  const { replace } = useAppNavigation();
  const searchParams = useQuery();

  // Helper function to restore search params
  const restoreSearchParamValueFor = useCallback((key: SCHEDULE_SEARCH_PARAMS, searchParams: URLSearchParams) => {
    const restoredSearchParams = new URLSearchParams(localStorage.getItem(SCHEDULE_SEARCH_PARAMS_STORAGE_KEY));
    return restoredSearchParams.get(key) || searchParams.get(key);
  }, []);

  // COUNTRY GROUP
  const [countryGroupId, setCountryGroupId] = useState<string | undefined>(
    restoreSearchParamValueFor(SCHEDULE_SEARCH_PARAMS.COUNTRY_GROUP, searchParams),
  );
  // COUNTRY
  const [countryId, setCountryId] = useState<string | undefined>(
    restoreSearchParamValueFor(SCHEDULE_SEARCH_PARAMS.COUNTRY, searchParams),
  );
  // COUNTRY STATE
  const [countryStateId, setCountryStateId] = useState<string | undefined>(
    restoreSearchParamValueFor(SCHEDULE_SEARCH_PARAMS.COUNTRY_STATE, searchParams),
  );
  // VIEW
  const [view, setView] = useState<string>(
    restoreSearchParamValueFor(SCHEDULE_SEARCH_PARAMS.VIEW, searchParams) ?? 'email',
  );
  const handleViewChange = useCallback<ComponentProps<typeof ToggleButtonGroup>['onChange']>((event, value) => {
    if (value) setView(value);
  }, []);
  // DATE RANGE
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>(() => {
    const fromDate = restoreSearchParamValueFor(SCHEDULE_SEARCH_PARAMS.FROM_DATE, searchParams);
    const toDate = restoreSearchParamValueFor(SCHEDULE_SEARCH_PARAMS.TO_DATE, searchParams);

    const today = getDayJs().tz(window.configs.DEFAULT_TIMEZONE);
    const start = (fromDate ? getDayJs(fromDate) : today).tz(window.configs.DEFAULT_TIMEZONE);
    const end = (toDate ? getDayJs(toDate) : today.add(1, 'week')).tz(window.configs.DEFAULT_TIMEZONE);
    return [start, end];
  });
  const handleDateRangeChange = useCallback(([start, end]: DateRange<Dayjs>) => {
    if (start?.isValid() && end?.isValid()) setDateRange([start, end]);
  }, []);
  const [fromDate, toDate] = dateRange;
  const sendDateForActions = (fromDate?.isValid() ? fromDate : getDayJs().tz(window.configs.DEFAULT_TIMEZONE)).format(
    'YYYY-MM-DD',
  );
  const { user } = useCurrentUser();
  const {
    isToggled: isRegenerateDialogOpen,
    toggleOn: openRegenerateDialog,
    toggleOff: closeRegenerateDialog,
  } = useToggleState(false);

  // SET BRAND PARAMS
  const previousBrandRef = useRef<string>(brand);
  useEffect(() => {
    if (previousBrandRef.current !== undefined && previousBrandRef.current === brand) {
      return;
    }
    // only run if brand is changed
    switch (brand) {
      case 'cudotravel':
      case 'dealstravel':
      case 'scoopontravel': {
        setCountryGroupId('australia');
        setCountryId('AU');
        break;
      }
      default: {
        setCountryGroupId(undefined);
        setCountryId(undefined);
        setCountryStateId(undefined);
      }
    }
    previousBrandRef.current = brand;
  }, [brand]);

  // SYNC URL SEARCH PARAMS
  useEffect(() => {
    if (fromDate && countryGroupId && toDate && view) {
      const newSearchParams = new URLSearchParams();

      if (countryGroupId) newSearchParams.set(SCHEDULE_SEARCH_PARAMS.COUNTRY_GROUP, countryGroupId);
      if (countryId) newSearchParams.set(SCHEDULE_SEARCH_PARAMS.COUNTRY, countryId);
      if (countryStateId && isCountryStateFilterSupportedForBrand(brand)) {
        newSearchParams.set(SCHEDULE_SEARCH_PARAMS.COUNTRY_STATE, countryStateId);
      }
      if (view) newSearchParams.set(SCHEDULE_SEARCH_PARAMS.VIEW, view);
      if (fromDate.isValid()) newSearchParams.set(SCHEDULE_SEARCH_PARAMS.FROM_DATE, fromDate.format('YYYY-MM-DD'));
      if (toDate.isValid()) newSearchParams.set(SCHEDULE_SEARCH_PARAMS.TO_DATE, toDate.format('YYYY-MM-DD'));

      // PERSIST IN STORAGE TO RECOVER UPON REFRESH
      localStorage.setItem(SCHEDULE_SEARCH_PARAMS_STORAGE_KEY, newSearchParams.toString());

      replace(`/customer-communication/hero-planner/schedules?${newSearchParams.toString()}`);
    }
  }, [fromDate, replace, countryGroupId, toDate, view, countryStateId, countryId, brand]);

  return (
    <Stack direction="column" gap={2}>
      <Stack direction="row" gap={4} justifyContent="space-between" alignItems="center" flexWrap="wrap-reverse">
        <Stack direction="row" columnGap={4} rowGap={3} flexWrap="wrap" alignItems="flex-start">
          <Stack direction="column" gap={1}>
            <DateRangePicker
              value={dateRange}
              localeText={{ start: 'From', end: 'To' }}
              formatDensity="spacious"
              format="DD/MM/YYYY"
              onChange={handleDateRangeChange}
            />
            <Stack direction="row" gap={1}>
              {DATE_PRESETS.map((preset) => {
                const [presetStart, presetEnd] = preset.getRange();
                const isActive =
                  fromDate?.isValid() &&
                  toDate?.isValid() &&
                  fromDate.isSame(presetStart, 'day') &&
                  toDate.isSame(presetEnd, 'day');
                return (
                  <Chip
                    key={preset.label}
                    label={preset.label}
                    size="small"
                    color={isActive ? 'primary' : 'default'}
                    variant={isActive ? 'filled' : 'outlined'}
                    onClick={() => setDateRange(preset.getRange())}
                  />
                );
              })}
            </Stack>
          </Stack>

          <ToggleButtonGroup value={view} exclusive size="small" onChange={handleViewChange} sx={{ mt: 1.75 }}>
            {VIEWS.map((viewOption) => (
              <ToggleButton key={viewOption.id} value={viewOption.id}>
                {viewOption.name}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" gap={1}>
          <Button
            variant="text"
            startIcon={<BugReportIcon />}
            component={Link}
            to={(() => {
              // open on the country group being looked at; the debugger checks one group at a time
              const params = new URLSearchParams({ [HERO_PLANNER_DEBUG_SEARCH_PARAMS.SEND_DATE]: sendDateForActions });
              if (countryGroupId) {
                params.set(HERO_PLANNER_DEBUG_SEARCH_PARAMS.COUNTRY_GROUP, countryGroupId);
              }
              return `${HERO_PLANNER_DEBUG_PATH}?${params.toString()}`;
            })()}
          >
            Debug
          </Button>
          <Button
            variant="contained"
            component={Link}
            to={(() => {
              const params = new URLSearchParams();
              if (fromDate?.isValid()) params.set(SCHEDULE_SEARCH_PARAMS.FROM_DATE, fromDate.format('YYYY-MM-DD'));
              if (countryGroupId) params.set(SCHEDULE_SEARCH_PARAMS.COUNTRY_GROUP, countryGroupId);
              if (countryId) params.set(SCHEDULE_SEARCH_PARAMS.COUNTRY, countryId);
              if (countryStateId && isCountryStateFilterSupportedForBrand(brand)) {
                params.set(SCHEDULE_SEARCH_PARAMS.COUNTRY_STATE, countryStateId);
              }
              return `/customer-communication/hero-planner/schedules/new?${params.toString()}`;
            })()}
          >
            Create Send
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" gap={2} alignItems="center">
        {/* minWidth 0 lets the scrollable tabs shrink instead of pushing the button off the row */}
        <Box flex={1} minWidth={0}>
          {isCountryGroupsFilterSupportedForBrand(brand) && (
            <SchedulesCountryGroupsTabs brand={brand} value={countryGroupId} onChange={setCountryGroupId} />
          )}
          {!isCountryGroupsFilterSupportedForBrand(brand) && (
            <SchedulesCountryStateTabs
              countryGroupId={countryGroupId}
              countryId={countryId}
              brand={brand}
              value={countryStateId}
              onChange={setCountryStateId}
            />
          )}
        </Box>
        {/* still a production pilot, so the role alone isn't enough */}
        {canRegenerateHeroPlannerTailDeals(user) && (
          <PermissionedComponent requiredRoles={[ROLE_HERO_PLANNER_ADMIN]}>
            {/* deliberately not primary: an admin-only action that should stand out from the filters around it */}
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<AutorenewIcon />}
              onClick={openRegenerateDialog}
              sx={{ flexShrink: 0 }}
            >
              Regenerate tail deals
            </Button>
            <RegenerateTailDealsDialog
              open={isRegenerateDialogOpen}
              onClose={closeRegenerateDialog}
              brand={brand}
              defaultSendDate={sendDateForActions}
              defaultCountryGroupId={countryGroupId}
            />
          </PermissionedComponent>
        )}
      </Stack>
    </Stack>
  );
}

export default SchedulesFilters;
